import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Share2, Download, Copy, Loader2, MessageCircle, Twitter, Facebook, Link2, Palette, Mail,
} from 'lucide-react';
import { generateJournalCard, CARD_THEMES } from './ShareCardRenderer';
import { isNative, nativeShare } from '../utils/capacitor';

const ThemeSelector = ({ activeTheme, onSelect }) => (
  <div className="flex items-center gap-2" data-testid="journal-theme-selector">
    <Palette className="w-4 h-4 text-ocean/50 flex-shrink-0" />
    <div className="flex gap-2">
      {Object.values(CARD_THEMES).map((t) => (
        <button
          key={t.id}
          data-testid={`journal-theme-${t.id}`}
          onClick={() => onSelect(t.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border-2 ${
            activeTheme === t.id
              ? 'border-ocean shadow-md scale-105'
              : 'border-transparent hover:border-ocean/20 opacity-70 hover:opacity-100'
          }`}
          style={{ background: t.swatch[0], color: t.swatch[1] }}
        >
          <span className="w-2.5 h-2.5 rounded-full border border-white/30" style={{ background: t.swatch[1] }} />
          {t.name}
        </button>
      ))}
    </div>
  </div>
);

// Pre-fetch image as base64 data URL to avoid Canvas CORS tainting in production.
// Uses a dedicated JSON endpoint that CORS middleware handles reliably,
// plus a same-origin fallback for maximum compatibility.
const fetchPhotoAsDataUrl = async (url) => {
  if (!url) return null;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  // Extract storage path and auth token from the photo URL
  // URL format: {origin}/api/journal/file/{storagePath}?auth={token}
  const extractParts = (photoUrl) => {
    try {
      const u = new URL(photoUrl);
      const match = u.pathname.match(/\/api\/journal\/file\/(.+)/);
      if (!match) return null;
      return { storagePath: match[1], auth: u.searchParams.get('auth') };
    } catch { return null; }
  };

  const parts = extractParts(url);
  if (!parts || !parts.auth) return null;

  // Use the base64 JSON endpoint — works reliably with CORS middleware
  const b64Path = `/api/journal/photo-b64/${parts.storagePath}?auth=${parts.auth}`;

  // Try with REACT_APP_BACKEND_URL first, then same-origin fallback
  const origins = [process.env.REACT_APP_BACKEND_URL, window.location.origin];
  const uniqueOrigins = [...new Set(origins.filter(Boolean))];

  for (const origin of uniqueOrigins) {
    try {
      const res = await fetch(`${origin}${b64Path}`);
      if (res.ok) {
        const json = await res.json();
        if (json.data_url) return json.data_url;
      }
    } catch { /* try next origin */ }
  }

  return null;
};

export const JournalShareDialog = ({ open, onOpenChange, photoUrl, destination, country, date, photoCount, galleryUrl }) => {
  const [shareImage, setShareImage] = useState(null);
  const [shareText, setShareText] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTheme, setActiveTheme] = useState('ocean');

  const buildCard = useCallback(async (themeId) => {
    try {
      // Convert remote photo URL to base64 data URL before Canvas rendering
      // Data URLs are same-origin and never taint the canvas
      const photoDataUrl = await fetchPhotoAsDataUrl(photoUrl);
      const dataUrl = await generateJournalCard(
        photoDataUrl, destination, country, date, photoCount, galleryUrl, themeId
      );
      if (dataUrl) {
        setShareImage(dataUrl);
        setShareText(
          `Check out my photos from ${destination}${country ? `, ${country}` : ''}! ${photoCount} travel memories captured. View the gallery: ${galleryUrl || 'tripleabooking.com'}`
        );
      } else {
        toast.error('Failed to generate card.');
      }
    } catch (err) {
      toast.error('Card generation failed.');
    } finally {
      setLoading(false);
    }
  }, [photoUrl, destination, country, date, photoCount, galleryUrl]);

  const openRef = useRef(open);
  useEffect(() => {
    const wasOpen = openRef.current;
    openRef.current = open;
    if (open && !wasOpen) {
      setLoading(true);
      buildCard('ocean');
    }
    if (!open && wasOpen) {
      setShareImage(null);
      setShareText('');
      setLoading(false);
      setActiveTheme('ocean');
    }
  }, [open, buildCard]);

  const handleThemeChange = async (themeId) => {
    setActiveTheme(themeId);
    setLoading(true);
    await buildCard(themeId);
  };

  const downloadImage = () => {
    if (!shareImage) return;
    const link = document.createElement('a');
    link.href = shareImage;
    link.download = `${(destination || 'journal').replace(/\s+/g, '-')}-journal-card.png`;
    link.click();
    toast.success('Image downloaded!');
  };

  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(shareText); toast.success('Caption copied!'); }
    catch { toast.error('Failed to copy'); }
  };

  const shareNative = async () => {
    if (isNative) {
      const shared = await nativeShare.share({
        title: `My ${destination} Photos`,
        text: shareText,
        url: galleryUrl || '',
      });
      if (shared) return;
    }
    if (!navigator.share) { toast.error('Native sharing not supported'); return; }
    try {
      const shareData = { title: `My ${destination} Photos`, text: shareText };
      if (shareImage) {
        try {
          const resp = await fetch(shareImage);
          const blob = await resp.blob();
          const file = new File([blob], 'journal-card.png', { type: 'image/png' });
          if (navigator.canShare?.({ files: [file] })) shareData.files = [file];
        } catch {}
      }
      await navigator.share(shareData);
    } catch (err) { if (err.name !== 'AbortError') toast.error('Sharing failed'); }
  };

  const shareWhatsApp = () => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, '_blank');
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank');
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(shareText)}`, '_blank');
  const shareEmail = () => window.open(`mailto:?subject=${encodeURIComponent(`My ${destination} Photo Journal`)}&body=${encodeURIComponent(shareText)}`, '_blank');
  const copyLink = async () => {
    if (galleryUrl) {
      try { await navigator.clipboard.writeText(galleryUrl); toast.success('Gallery link copied!'); }
      catch { toast.error('Failed to copy link'); }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl bg-white/95 backdrop-blur-xl max-h-[90vh] overflow-y-auto" data-testid="journal-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-ocean flex items-center gap-2">
            <Share2 className="w-5 h-5 text-gold" />
            Share Photo Journal
          </DialogTitle>
          <DialogDescription>Share your {destination} photo journal</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <ThemeSelector activeTheme={activeTheme} onSelect={handleThemeChange} />

          <div className="relative rounded-xl overflow-hidden border border-ocean/10 shadow-md bg-ocean/5" data-testid="journal-share-card-container">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-ocean mb-3" />
                <p className="text-ocean/60 text-sm">Creating journal card...</p>
              </div>
            ) : shareImage ? (
              <img src={shareImage} alt={`${destination} journal`} className="w-full h-auto" data-testid="journal-share-card-image" />
            ) : (
              <div className="flex items-center justify-center py-16">
                <p className="text-ocean/40">Failed to generate card</p>
              </div>
            )}
          </div>

          {shareText && (
            <div className="bg-ocean/5 rounded-xl p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-charcoal flex-1">{shareText}</p>
                <Button variant="ghost" size="icon" onClick={copyCaption} className="h-7 w-7 flex-shrink-0 text-ocean/50 hover:text-ocean" data-testid="copy-journal-caption-btn">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            {navigator.share && (
              <Button onClick={shareNative} className="bg-ocean hover:bg-ocean-light text-white rounded-xl h-11 col-span-2" data-testid="journal-native-share-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
            )}
            <Button onClick={shareWhatsApp} variant="outline" className="rounded-xl h-11 border-green-300 text-green-700 hover:bg-green-50" data-testid="journal-whatsapp-btn">
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
            <Button onClick={shareTwitter} variant="outline" className="rounded-xl h-11 border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="journal-twitter-btn">
              <Twitter className="w-4 h-4 mr-2" /> Twitter / X
            </Button>
            <Button onClick={shareFacebook} variant="outline" className="rounded-xl h-11 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="journal-facebook-btn">
              <Facebook className="w-4 h-4 mr-2" /> Facebook
            </Button>
            <Button onClick={shareEmail} variant="outline" className="rounded-xl h-11 border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="journal-email-btn">
              <Mail className="w-4 h-4 mr-2" /> Email
            </Button>
            {galleryUrl && (
              <Button onClick={copyLink} variant="outline" className="rounded-xl h-11 border-ocean/20 text-ocean hover:bg-ocean/5" data-testid="journal-copy-link-btn">
                <Link2 className="w-4 h-4 mr-2" /> Copy Gallery Link
              </Button>
            )}
            {shareImage && (
              <Button onClick={downloadImage} variant="outline" className="rounded-xl h-11 border-ocean/30 text-ocean hover:bg-ocean/5 font-semibold" data-testid="download-journal-card-btn">
                <Download className="w-4 h-4 mr-2" /> Download Card
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
